import { useState, useEffect } from "react"
import { useQuery } from "@tanstack/react-query"
import { Card, Divider, Button, Segmented, Tag, Space, Badge } from "antd"
import { ReloadOutlined, TableOutlined, CodeOutlined, CloudServerOutlined } from "@ant-design/icons"
import type React from "react"
import SystemLogFilterForm from "./components/SystemLogFilterForm"
import SystemLogTable from "./components/SystemLogTable"
import SystemLogTerminalView from "./components/SystemLogTerminalView"
import SystemLogDetailDrawer from "./components/SystemLogDetailDrawer"
import { systemLogService, type SystemLogQueryParams, type SystemLogItem } from "@/services/log/systemLogApi"
import { PAGE_CONFIG, PAGINATION_CONFIG } from "@/constants/table"
import { useTableParams } from "@/hooks/useTableParams"

interface SystemLogPageProps {
  defaultService?: string
}

export const SystemLogPage: React.FC<SystemLogPageProps> = ({ defaultService = "web-api" }) => {
  const [activeService, setActiveService] = useState<string>(defaultService)
  const [viewMode, setViewMode] = useState<"table" | "terminal">("table")
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number>(0)
  const [selectedRecord, setSelectedRecord] = useState<SystemLogItem | null>(null)

  // 1. Fetch available services
  const { data: servicesResult } = useQuery({
    queryKey: ["system_log_services"],
    queryFn: () => systemLogService.getServices(),
  })

  const services = servicesResult?.data || []
  const currentServiceObj = services.find((s) => s.id === activeService)
  const serviceDisplayName = currentServiceObj?.name || (activeService === "web-api" ? "Web API" : activeService)

  // 2. Fetch log files for active service
  const { data: filesResult, isFetching: isFetchingFiles } = useQuery({
    queryKey: ["system_log_files", activeService],
    queryFn: () => systemLogService.getFiles(activeService),
  })

  const files = filesResult?.data || []
  const defaultFile = files[0]?.name || ""

  // 3. Table params & search
  const [searchParams, setSearchParams] = useTableParams<SystemLogQueryParams>({
    ...PAGE_CONFIG,
    per_page: 50,
    file: defaultFile,
    level: "all",
    status_code: "all",
    search: "",
  })

  // Synchronize default file when files loaded
  useEffect(() => {
    if (!searchParams.file && defaultFile) {
      setSearchParams({
        ...searchParams,
        file: defaultFile,
      })
    }
  }, [defaultFile, searchParams, setSearchParams])

  // 4. Fetch logs
  const {
    data: logsResult,
    isFetching: isFetchingLogs,
    refetch,
  } = useQuery({
    queryKey: ["system_logs_data", activeService, searchParams],
    queryFn: () => {
      return systemLogService.list(activeService, {
        page: searchParams.page,
        limit: searchParams.per_page,
        file: searchParams.file || undefined,
        level: searchParams.level !== "all" ? searchParams.level : undefined,
        status_code: searchParams.status_code !== "all" ? searchParams.status_code : undefined,
        search: searchParams.search || undefined,
      })
    },
    enabled: Boolean(searchParams.file || files.length > 0),
    refetchInterval: autoRefreshInterval > 0 ? autoRefreshInterval * 1000 : false,
  })

  const items = logsResult?.data || []
  const totalCount = logsResult?.total ?? 0
  const stats = logsResult?.stats
  const activeFileName = logsResult?.file || searchParams.file || defaultFile

  const handleSearch = (values: Partial<SystemLogQueryParams>) => {
    setSearchParams({
      ...searchParams,
      ...values,
      page: 1,
    })
  }

  const handleDownload = () => {
    if (!activeFileName) return
    const url = systemLogService.getDownloadUrl(activeService, activeFileName)
    window.open(url, "_blank")
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Service Switcher if multiple services are available */}
      {services.length > 1 && (
        <div className="mb-1 flex items-center gap-2">
          <span className="font-semibold text-gray-500 text-xs uppercase">Dịch vụ:</span>
          <Segmented
            value={activeService}
            onChange={(val) => {
              setActiveService(String(val))
              setSearchParams({ ...PAGE_CONFIG, per_page: 50, level: "all", status_code: "all", search: "" })
            }}
            options={services.map((s) => ({
              label: (
                <span className="flex items-center gap-1.5 px-2">
                  <CloudServerOutlined className={s.available ? "text-green-500" : "text-gray-400"} />
                  <span>{s.name}</span>
                  {!s.available && <Tag className="m-0 text-[10px]">offline</Tag>}
                </span>
              ),
              value: s.id,
              disabled: !s.available,
            }))}
          />
        </div>
      )}

      {/* Filter Form */}
      <SystemLogFilterForm
        files={files}
        currentFile={activeFileName}
        onSearch={handleSearch}
        onDownload={handleDownload}
        loading={isFetchingLogs || isFetchingFiles}
        initialValues={{
          file: searchParams.file,
          level: searchParams.level,
          status_code: searchParams.status_code,
          search: searchParams.search,
        }}
        autoRefreshInterval={autoRefreshInterval}
        onAutoRefreshChange={setAutoRefreshInterval}
      />

      {/* Card chứa danh sách logs */}
      <Card
        className="flex min-h-0 grow flex-col shadow-xs"
        classNames={{ body: "flex grow flex-col" }}
        title={
          <div className="flex flex-wrap items-center justify-between gap-3 py-1">
            <div className="flex items-center gap-2">
              <CloudServerOutlined className="text-blue-600 text-lg" />
              <span className="font-semibold text-base text-gray-800">Nhật ký {serviceDisplayName}</span>
              <Divider orientation="vertical" />
              <span className="font-mono font-normal text-gray-500 text-xs">
                File: <strong className="text-gray-700">{activeFileName || "—"}</strong>
              </span>

              {stats && (
                <Space size={6} className="ml-2">
                  <Badge
                    count={stats.error}
                    overflowCount={9999}
                    style={{ backgroundColor: "#f5222d" }}
                    title="Số lỗi Error"
                  />
                  <Badge
                    count={stats.warn}
                    overflowCount={9999}
                    style={{ backgroundColor: "#faad14" }}
                    title="Số cảnh báo Warn"
                  />
                  <Badge
                    count={stats.info}
                    overflowCount={9999}
                    style={{ backgroundColor: "#1677ff" }}
                    title="Số thông tin Info"
                  />
                </Space>
              )}
            </div>

            <div className="flex items-center gap-3">
              <Segmented
                value={viewMode}
                onChange={(val) => setViewMode(val as "table" | "terminal")}
                options={[
                  {
                    label: "Bảng",
                    value: "table",
                    icon: <TableOutlined />,
                  },
                  {
                    label: "Console",
                    value: "terminal",
                    icon: <CodeOutlined />,
                  },
                ]}
              />

              <Button
                icon={<ReloadOutlined spin={isFetchingLogs} />}
                onClick={() => refetch()}
                loading={isFetchingLogs}
              >
                Làm mới
              </Button>
            </div>
          </div>
        }
      >
        {viewMode === "table" ? (
          <SystemLogTable
            data={items}
            loading={isFetchingLogs}
            onViewDetail={(record) => setSelectedRecord(record)}
            pagination={{
              pageSize: searchParams.per_page,
              current: searchParams.page,
              total: totalCount,
              ...PAGINATION_CONFIG,
              onChange(page: number, pageSize: number) {
                setSearchParams({
                  ...searchParams,
                  page,
                  per_page: pageSize,
                })
              },
            }}
          />
        ) : (
          <SystemLogTerminalView
            data={items}
            loading={isFetchingLogs}
            onViewDetail={(record) => setSelectedRecord(record)}
          />
        )}
      </Card>

      {/* Drawer chi tiết log */}
      <SystemLogDetailDrawer
        open={Boolean(selectedRecord)}
        record={selectedRecord}
        onClose={() => setSelectedRecord(null)}
        serviceName={serviceDisplayName}
      />
    </div>
  )
}

export default SystemLogPage
